import lodash from 'lodash'
import { pi } from 'mathjs'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import { GeckoCoatingRegionJSON } from 'types/assetModel/features/geckoCoatingRegion'
import { GeckoLinerRegionJSON } from 'types/assetModel/features/geckoLinerRegion'
import {
  GeckoManway,
  GeckoManwayJSON,
} from 'types/assetModel/features/geckoManway'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import { GeckoPlateJSON } from 'types/assetModel/features/geckoPlate'
import { GeckoPolygonalPlateJSON } from 'types/assetModel/features/geckoPolygonalPlate'
import { GeckoCourseJSON } from 'types/assetModel/features/verticalTankShell/geckoShellCourse'
import { GeckoPipeCircumferentialWeldJSON } from 'types/assetModel/features/welds/geckoPipeCircumferentialWeld'
import { GeckoPipeSeamWeldJSON } from 'types/assetModel/features/welds/geckoPipeSeamWeld'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export const PressureVesselShellAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.PipeCircumferentialWeld,
  GeckoFeatureModelType.PipeSeamWeld,
  GeckoFeatureModelType.Nozzle,
  GeckoFeatureModelType.Manway,
  GeckoFeatureModelType.FixedSensor,
]

export type PressureVesselShellAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.PressureVesselShell
  length: number
  radius: number
  // TODO: figure out which features are needed for pressure vessel shell
  features: Record<
    string,
    | GeckoCourseJSON
    | GeckoPipeCircumferentialWeldJSON
    | GeckoPipeSeamWeldJSON
    | GeckoPlateJSON
    | GeckoNozzleJSON
    | GeckoManwayJSON
    | GeckoPolygonalPlateJSON
    | GeckoCoatingRegionJSON
    | GeckoLinerRegionJSON
  >
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  nominal: {
    wall_thickness: number | undefined
  }
  compass_orientation: number | undefined
  material: string | undefined
  installation_date: string | undefined
  lining_type: string | undefined
  coating_type: string | undefined
  coating_thickness: number | undefined
  insulation_type: string | undefined
  insulation_thickness: number | undefined
}

export const NewPressureVesselShellAssetModelJSON = {
  name: 'New Pressure Vessel Shell',
  type: GeckoComponentModelType.PressureVesselShell,
  length: 0,
  radius: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  nominal: {
    wall_thickness: undefined,
  },
  compass_orientation: undefined,
  material: undefined,
  installation_date: undefined,
  lining_type: undefined,
  coating_type: undefined,
  coating_thickness: undefined,
  insulation_type: undefined,
  insulation_thickness: undefined,
} as PressureVesselShellAssetModelJSON

export class GeckoPressureVesselShell extends GeckoEditableComponent {
  type: GeckoComponentModelType.PressureVesselShell
  radius: GeckoMeasurement
  length: GeckoMeasurement
  material: string | undefined
  installationDate: Date | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  nozzles: Map<string, GeckoNozzle>
  manways: Map<string, GeckoManway>
  wallNominalThickness: GeckoMeasurement | undefined
  compassOrientation: number | undefined
  liningType: string | undefined
  coatingType: string | undefined
  coatingThickness: GeckoMeasurement | undefined
  insulationType: string | undefined
  insulationThickness: GeckoMeasurement | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    length: GeckoMeasurement,
    material: string | undefined,
    installationDate: Date | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    nozzles: Map<string, GeckoNozzle>,
    manways: Map<string, GeckoManway>,
    wallNominalThickness: GeckoMeasurement | undefined,
    compassOrientation: number | undefined,
    liningType: string | undefined,
    coatingType: string | undefined,
    coatingThickness: GeckoMeasurement | undefined,
    insulationType: string | undefined,
    insulationThickness: GeckoMeasurement | undefined
  ) {
    super(id, name, json)

    this.type = GeckoComponentModelType.PressureVesselShell
    this.radius = radius
    this.length = length
    this.material = material
    this.installationDate = installationDate
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.nozzles = nozzles
    this.manways = manways
    this.wallNominalThickness = wallNominalThickness
    this.compassOrientation = compassOrientation
    this.liningType = liningType
    this.coatingType = coatingType
    this.coatingThickness = coatingThickness
    this.insulationType = insulationType
    this.insulationThickness = insulationThickness
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get manwaysArray() {
    return Array.from(this.manways.values())
  }

  public static CreateFromJSON(
    id: string,
    json: PressureVesselShellAssetModelJSON
  ) {
    const name = json.name
    const shellRadius = GeckoMeasurement.CreateFromValue(
      json.radius,
      GeckoMeasurementUnit.Meters
    )
    const shellLength = GeckoMeasurement.CreateFromValue(
      json.length,
      GeckoMeasurementUnit.Meters
    )

    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      json.component_bounds.x_start,
      json.component_bounds.y_start,
      json.component_bounds.x_end,
      json.component_bounds.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config

    const featureData = json.features

    const childNozzleJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    }) as Record<string, GeckoNozzleJSON>
    const nozzles = GeckoNozzle.CreateMapFromJSON(id, childNozzleJSON)

    const childManwayJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Manway
    }) as Record<string, GeckoManwayJSON>
    const manways = GeckoManway.CreateMapFromJSON(id, childManwayJSON)

    const parsedWallNominalThickness = parseNumber(json.nominal.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const compassOrientation = parseNumber(json.compass_orientation)

    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)
    const liningType = parseString(json.lining_type)
    const coatingType = parseString(json.coating_type)
    const parsedCoatingThickness = parseNumber(json.coating_thickness)
    const coatingThickness =
      parsedCoatingThickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedCoatingThickness,
            GeckoMeasurementUnit.Millimeters
          )
        : undefined
    const insulationType = parseString(json.insulation_type)
    const parsedInsulationThickness = parseNumber(json.insulation_thickness)
    const insulationThickness =
      parsedInsulationThickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedInsulationThickness,
            GeckoMeasurementUnit.Millimeters
          )
        : undefined

    return new GeckoPressureVesselShell(
      id,
      json,
      name,
      shellRadius,
      shellLength,
      material,
      installationDate,
      componentBounds,
      sceneConfig,
      nozzles,
      manways,
      wallNominalThickness,
      compassOrientation,
      liningType,
      coatingType,
      coatingThickness,
      insulationType,
      insulationThickness
    )
  }

  public asJSON(): PressureVesselShellAssetModelJSON {
    return generatePressureVesselShell(
      this.name,
      this.length,
      this.radius,
      this.wallNominalThickness,
      this.compassOrientation,
      this.json as PressureVesselShellAssetModelJSON
    )
  }
}

const generatePressureVesselShell = (
  name: string,
  length: GeckoMeasurement,
  radius: GeckoMeasurement,
  wallNominalThickness: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherShellData: PressureVesselShellAssetModelJSON = NewPressureVesselShellAssetModelJSON
): PressureVesselShellAssetModelJSON => {
  const shellLength = length
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const shellRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const shellCircumference = Number(
    (2 * pi * shellRadius).toPrecision(assetModelPrecision)
  )

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.PressureVesselShell,
    length: shellLength,
    radius: shellRadius,
    features: otherShellData.features,
    scene_config: otherShellData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: shellCircumference,
      y_end: shellLength,
    },
    nominal: {
      wall_thickness: wallNominalThicknessMeters,
    },
    compass_orientation: compassOrientation,
    material: otherShellData.material,
    installation_date: otherShellData.installation_date,
    lining_type: otherShellData.lining_type,
    coating_type: otherShellData.coating_type,
    coating_thickness: otherShellData.coating_thickness,
    insulation_type: otherShellData.insulation_type,
    insulation_thickness: otherShellData.insulation_thickness,
  }
}

export const PressureVesselShellAssetModelFormFields = (
  shellJSON: PressureVesselShellAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Shell Name',
      value: shellJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'length',
      label: 'Length / Height',
      value: shellJSON.length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'radius',
      label: 'Outer Diameter',
      value: shellJSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'wall_thickness',
      label: 'Shell Thickness (Nominal)',
      value: shellJSON.nominal.wall_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material Type',
      value: shellJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'installation_date',
      label: 'Installation Date',
      value: shellJSON.installation_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Date,
    },
    {
      dataKey: 'lining_type',
      label: 'Lining Type',
      value: shellJSON.lining_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coating_type',
      label: 'Coating Type',
      value: shellJSON.coating_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coating_thickness',
      label: 'Coating Thickness',
      value: shellJSON.coating_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'insulation_type',
      label: 'Insulation Type',
      value: shellJSON.insulation_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'insulation_thickness',
      label: 'Insulation Thickness',
      value: shellJSON.insulation_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const PressureVesselShellJSONFromFormFields = (
  component: PressureVesselShellAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )

  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )

  const updatedComponent: PressureVesselShellAssetModelJSON = {
    ...component,
    name: getFormFieldByDataKey(formFields, 'name')?.value as string,
    length: getFormFieldByDataKey(formFields, 'length')?.value as number,
    radius: (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2,
    nominal: {
      wall_thickness: parsedWallNominalThickness,
    },
    compass_orientation: compassOrientation,
    material: parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    installation_date: parseString(
      getFormFieldByDataKey(formFields, 'installation_date')?.value
    ),
    lining_type: parseString(
      getFormFieldByDataKey(formFields, 'lining_type')?.value
    ),
    coating_type: parseString(
      getFormFieldByDataKey(formFields, 'coating_type')?.value
    ),
    coating_thickness: parseNumber(
      getFormFieldByDataKey(formFields, 'coating_thickness')?.value
    ),
    insulation_type: parseString(
      getFormFieldByDataKey(formFields, 'insulation_type')?.value
    ),
    insulation_thickness: parseNumber(
      getFormFieldByDataKey(formFields, 'insulation_thickness')?.value
    ),
  }

  return updatedComponent
}
